import type { NetMessage } from './transport';
import type { Element } from '../types/enums';
import { netService } from '../services/NetService';

export interface ProjectileSpawnEvent extends NetMessage {
  type: 'projectile-spawn';
  id: string;
  sprite: string;
  originX: number;
  originY: number;
  direction: number;
  speed: number;
  range: number;
}

export interface ProjectileDespawnEvent extends NetMessage {
  type: 'projectile-despawn';
  id: string;
}

export interface VfxEvent extends NetMessage {
  type: 'vfx';
  id: string;
  spriteName: string;
  x: number;
  y: number;
  duration: number;
  direction?: number;
  isLooping?: boolean;
  animationSpeed?: number | 'auto';
  width?: number;
  height?: number;
  scale?: number;
  anchor?: [number, number];
  rotation?: number;
  parallaxDepth?: number;
}

export interface VfxDespawnEvent extends NetMessage {
  type: 'vfx-despawn';
  id: string;
}

export interface PreviewAddEvent extends NetMessage {
  type: 'preview-add';
  groupId: string;
  entityId: string;
  cells: { x: number; y: number }[];
  duration: number;
  isPlayer: boolean;
  castSeq?: number;
  color?: number;
  alpha?: number;
  previewType?: string;
  noProgressFill?: boolean;
}

export interface PreviewRemoveEvent extends NetMessage {
  type: 'preview-remove';
  groupId?: string;
  entityId?: string;
}

// castSeq counters are per-client, so a broadcast cast-rejected carries entityId;
// the client acts only on its own (preview-add is already disambiguated by entityId).
export interface CastRejectedEvent extends NetMessage {
  type: 'cast-rejected';
  entityId: string;
  castSeq: number;
}

export type DamageDisplayKind =
  | 'damage'
  | 'shieldDamage'
  | 'healHp'
  | 'healShield';

export interface DamageNumberEvent extends NetMessage {
  type: 'damage-number';
  targetId: string;
  value: number;
  worldX: number;
  worldY: number;
  display: DamageDisplayKind;
  crit?: boolean;
  element?: Element;
}

const COMBAT_EVENT_TYPES = new Set([
  'projectile-spawn',
  'projectile-despawn',
  'vfx',
  'vfx-despawn',
  'damage-number',
  'preview-add',
  'preview-remove',
  'cast-rejected',
]);

export function isCombatEvent(msg: NetMessage): boolean {
  return COMBAT_EVENT_TYPES.has(msg.type);
}

// All senders are no-ops off the host; the client never re-broadcasts.
function broadcast(msg: NetMessage): void {
  if (!netService.isHost()) {
    return;
  }
  netService.session?.broadcast('reliable', msg);
}

export function sendProjectileSpawn(
  e: Omit<ProjectileSpawnEvent, 'type'>,
): void {
  broadcast({ type: 'projectile-spawn', ...e });
}

export function sendProjectileDespawn(id: string): void {
  broadcast({ type: 'projectile-despawn', id });
}

export function sendVfx(e: Omit<VfxEvent, 'type'>): void {
  broadcast({ type: 'vfx', ...e });
}

export function sendVfxDespawn(id: string): void {
  broadcast({ type: 'vfx-despawn', id });
}

export function sendPreviewAdd(e: Omit<PreviewAddEvent, 'type'>): void {
  broadcast({ type: 'preview-add', ...e });
}

export function sendPreviewRemove(e: Omit<PreviewRemoveEvent, 'type'>): void {
  broadcast({ type: 'preview-remove', ...e });
}

export function sendCastRejected(e: Omit<CastRejectedEvent, 'type'>): void {
  broadcast({ type: 'cast-rejected', ...e });
}

export function sendDamageNumber(e: Omit<DamageNumberEvent, 'type'>): void {
  broadcast({ type: 'damage-number', ...e });
}
